// Validation helpers for the Security Service Provider application.
//
// These return translation KEYS, not sentences. The form is available in six
// languages, so a validator that returned "City is required" would silently
// pin every error message to English no matter which language the applicant
// chose. Callers pass whatever comes back through t().
//
// File-selection errors additionally need interpolation values, so they come
// back as { key, params } and are rendered with t(key, params).

export const PHONE_ERROR = 'validation.phoneRequired';

export function normalizePhone(value) {
  return String(value || '').replace(/\D/g, '');
}

export function isTenDigitPhone(value) {
  return normalizePhone(value).length === 10;
}

export const FILE_RULES = {
  id: { extensions: ['.pdf', '.jpg', '.jpeg', '.png'], labelKey: 'fileTypes.pdfImages', maxCount: 1 },
  license: { extensions: ['.pdf', '.jpg', '.jpeg', '.png'], labelKey: 'fileTypes.pdfImages', maxCount: 3 },
  certification: { extensions: ['.pdf', '.jpg', '.jpeg', '.png'], labelKey: 'fileTypes.pdfImages', maxCount: 5 },
  other: { extensions: ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'], labelKey: 'fileTypes.pdfDocsImages', maxCount: 5 },
};

export const MAX_FILE_BYTES = 50 * 1024 * 1024;

export const MAX_TOTAL_FILES = 10;

export function validateFileSelection(type, selectedFiles, currentFiles) {
  const errors = [];
  if (!type || !FILE_RULES[type]) return { ok: true, errors };
  const rules = FILE_RULES[type];
  const selected = Array.isArray(selectedFiles) ? selectedFiles : [];
  const current = currentFiles && typeof currentFiles === 'object' ? currentFiles : {};
  const currentArr = type === 'id' ? (current[type] ? [current[type]] : []) : (Array.isArray(current[type]) ? current[type] : []);

  for (const file of selected) {
    if (!file || !file.name) continue;
    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!rules.extensions.includes(ext)) {
      errors.push({ key: 'toast.fileWrongType', params: { name: file.name, allowed: rules.labelKey } });
    } else if (file.size > MAX_FILE_BYTES) {
      errors.push({ key: 'toast.fileTooLarge', params: { name: file.name } });
    }
  }

  if (!errors.length) {
    const currentCount = currentArr.length;
    const newTotal = type === 'id' ? selected.length : (currentCount + selected.length);
    if (newTotal > rules.maxCount) {
      errors.push({ key: 'toast.fileTooManyOfType', params: { count: rules.maxCount } });
    } else {
      const totalFiles = (current.id ? 1 : 0) +
        (Array.isArray(current.license) ? current.license.length : 0) +
        (Array.isArray(current.certification) ? current.certification.length : 0) +
        (Array.isArray(current.other) ? current.other.length : 0);
      const addedCount = selected.length;
      const removedCount = type === 'id' && currentArr.length > 0 ? 1 : 0;
      const finalTotal = totalFiles - removedCount + addedCount;
      if (finalTotal > MAX_TOTAL_FILES) {
        errors.push({ key: 'toast.fileTooManyTotal', params: { count: MAX_TOTAL_FILES } });
      }
    }
  }

  return { ok: errors.length === 0, errors };
}

export function validateLicenseEntries(licenses) {
  const arr = Array.isArray(licenses) ? licenses : [];
  const today = new Date().toISOString().split('T')[0];
  return arr.map(entry => {
    if (!entry || typeof entry !== 'object') return null;
    const type = (entry.type || '').toString().trim();
    const otherType = (entry.otherType || '').toString().trim();
    const number = (entry.number || '').toString().trim();
    const issuingAuthority = (entry.issuingAuthority || '').toString().trim();
    const otherAuthority = (entry.otherAuthority || '').toString().trim();
    const expiryDate = (entry.expiryDate || '').toString().trim();
    const isCompleteEmpty = !type && !otherType && !number && !issuingAuthority && !otherAuthority && !expiryDate;
    if (isCompleteEmpty) return null;

    // Military/Law Enforcement service isn't a numbered license — the type
    // alone qualifies; details are collected in the Skills checklist. This
    // matches against the canonical English value from LICENSE_TYPES, which is
    // never translated, so the check holds in every language.
    if (type.startsWith('Military/Law Enforcement')) {
      if (expiryDate && expiryDate < today) {
        return 'validation.licenseExpired';
      }
      return null;
    }

    if (!type || !number || !issuingAuthority || !expiryDate) {
      return 'validation.licenseIncomplete';
    }
    if (type === 'other' && !otherType) {
      return 'validation.licenseTypeUnspecified';
    }
    if (issuingAuthority === 'other' && !otherAuthority) {
      return 'validation.licenseAuthorityUnspecified';
    }
    if (expiryDate < today) {
      return 'validation.licenseExpired';
    }
    return null;
  });
}

export function validateTrainingEntries(training) {
  const arr = Array.isArray(training) ? training : [];
  return arr.map(entry => {
    if (!entry || typeof entry !== 'object') return null;
    const courseName = (entry.courseName || '').toString().trim();
    const isCompleteEmpty = !courseName && !(entry.institution || '').toString().trim() && !(entry.completionDate || '').toString().trim();
    if (isCompleteEmpty) return null;

    if (!courseName) {
      return 'validation.trainingIncomplete';
    }
    return null;
  });
}

export function validateMartialArtsEntries(martialArts) {
  const arr = Array.isArray(martialArts) ? martialArts : [];
  return arr.map(entry => {
    if (!entry || typeof entry !== 'object') return null;
    const style = (entry.style || '').toString().trim();
    const isCompleteEmpty = !style && !(entry.level || '').toString().trim() && !(entry.yearsExperience || '').toString().trim();
    if (isCompleteEmpty) return null;

    if (!style) {
      return 'validation.martialArtIncomplete';
    }
    return null;
  });
}

export function validateLanguageEntries(languages) {
  const arr = Array.isArray(languages) ? languages : [];
  return arr.map(entry => {
    if (!entry || typeof entry !== 'object') return null;
    const language = (entry.language || '').toString().trim();
    const otherLanguage = (entry.otherLanguage || '').toString().trim();
    const proficiency = (entry.proficiency || '').toString().trim();
    const isCompleteEmpty = !language && !otherLanguage && !proficiency;
    if (isCompleteEmpty) return null;

    if (language === 'other' && !otherLanguage) {
      return 'validation.languageUnspecified';
    }
    return null;
  });
}

export function firstErrorIndex(errors) {
  const arr = Array.isArray(errors) ? errors : [];
  return arr.indexOf(arr.find(e => e !== null));
}
